// Emergent Google Auth integration for CaratJewel Expo.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
//
// Flow:
//   1. `startGoogleSignIn()` redirects to auth.emergentagent.com with
//      window.location.origin + "/" as the redirect target.
//   2. Emergent returns to the origin with #session_id=... in the URL fragment.
//   3. `AuthCallback` exchanges that with our backend `/api/auth/session`, which
//      sets an httpOnly cookie and returns the user profile with a role.
//   4. `AuthProvider` exposes { user, loading, refresh, signOut }.
//
// The provider is careful to skip the /auth/me probe when a fresh
// session_id is present in the URL — the callback will exchange it first.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

async function apiJson(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (res.status === 403 && body?.detail?.code === "admin_email_verification_required") {
      throw Object.assign(new Error("Administrator sign-in is blocked because the identity provider did not confirm email verification. The sign-in integration needs attention; retrying will not resolve this."), {code: "admin_email_verification_required"});
    }
    throw new Error("We couldn’t complete sign-in. Please try again.");
  }
  return res.status === 204 ? null : res.json();
}

export function startGoogleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiJson("/auth/me");
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Skip the /auth/me probe if we're mid-callback — AuthCallback will exchange
    // the session_id first and then call refresh() itself.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await apiJson("/auth/logout", { method: "POST" });
    } catch { /* clear state regardless */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// Route-level fragment interception. Rendered BEFORE the normal <Routes/> so
// that a fresh #session_id=... is exchanged before ProtectedRoute redirects.
export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current === location.hash) return;
    processed.current = location.hash;
    const callbackHash = location.hash;
    setError("");
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const ids = params.getAll("session_id");
    if (ids.length !== 1 || !ids[0] || /\s/.test(ids[0])) {
      setError("We couldn’t complete sign-in. Please try again.");
      return;
    }
    const sessionId = ids[0];
    (async () => {
      try {
        await apiJson("/auth/session", {
          method: "POST",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (processed.current !== callbackHash) return;
        // Clear the hash from the URL immediately so a refresh doesn't re-run.
        window.history.replaceState({}, "", window.location.pathname);
        const user = await refresh();
        if (processed.current !== callbackHash) return;
        if (!user) throw new Error("We couldn’t complete sign-in. Please try again.");
        const target =
          user.role === "admin" ? "/admin" :
          user.role === "exhibitor" ? "/dashboard/exhibitor" :
          "/dashboard/buyer";
        navigate(target, { replace: true, state: { user } });
      } catch (e) {
        if (processed.current !== callbackHash) return;
        setError(e.code === "admin_email_verification_required" ? e.message : "We couldn’t complete sign-in. Please try again.");
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="auth-callback" data-testid="auth-callback">
      <div className="auth-callback-inner">
        <div className="eyebrow">SIGNING YOU IN</div>
        <h2>{error ? "Something went wrong." : "One moment…"}</h2>
        {error && <p data-testid="auth-callback-error">{error}</p>}
        {error && <button type="button" onClick={startGoogleSignIn}>Try signing in again</button>}
      </div>
      <style>{`
        .auth-callback{min-height:100vh;display:grid;place-items:center;background:#fbfbfa;padding:40px 20px}
        .auth-callback-inner{text-align:center;max-width:420px}
        .auth-callback-inner h2{font:500 32px/1.1 'Manrope',sans-serif;margin:12px 0}
        .auth-callback-inner p{font-size:12px;color:#a05a4a}
      `}</style>
    </div>
  );
}

// Guard component. Renders `fallback` (or a full-screen loader) while auth
// state is loading. If the user is missing or does not have one of `roles`,
// renders the `denied` node (or redirects to /login by default).
export function ProtectedRoute({ children, roles, denied }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user && !denied) navigate("/login", { replace: true });
  }, [loading, user, denied, navigate]);
  if (loading) {
    return (
      <div className="auth-loading" data-testid="auth-loading">
        Loading your workspace…
        <style>{`.auth-loading{min-height:60vh;display:grid;place-items:center;font:10px 'DM Mono',monospace;color:#888}`}</style>
      </div>
    );
  }
  if (!user) return denied ?? null;
  if (roles && !roles.includes(user.role)) {
    return denied ?? (
      <div className="auth-denied" data-testid="auth-denied">
        <div>
          <div className="eyebrow">NOT AUTHORIZED</div>
          <h2>Your workspace lives elsewhere.</h2>
          <p>Your account is signed in as <strong>{user.role}</strong>. This area is reserved for {roles.join(" / ")}.</p>
        </div>
        <style>{`
          .auth-denied{min-height:60vh;display:grid;place-items:center;padding:60px 6vw;text-align:center}
          .auth-denied h2{font:500 32px/1.1 'Manrope',sans-serif;margin:10px 0 12px}
          .auth-denied p{font-size:13px;color:#777}
          .auth-denied strong{color:var(--ink)}
        `}</style>
      </div>
    );
  }
  return children;
}
